import { useState, useEffect, useRef, useCallback } from 'react'
import { Upload, RefreshCw, Trash2, CheckCircle2, XCircle, Clock, Loader2, ChevronDown, ChevronUp, Box, RotateCcw, ExternalLink, AlertTriangle } from 'lucide-react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { Button } from '@/components/ui/Button'
import {
  createPipelineJob,
  fetchPipelineJobs,
  fetchAdminModels,
  deletePipelineJob,
  retryPipelineJob,
  getAssetUrl,
  type PipelineJob,
} from '@/lib/api'
import type { Model } from '@/types'

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending:    { icon: Clock,        color: 'text-slate-500 bg-slate-100',   label: 'Pending'    },
  processing: { icon: Loader2,      color: 'text-blue-600 bg-blue-50',      label: 'Processing' },
  done:       { icon: CheckCircle2, color: 'text-green-700 bg-green-50',    label: 'Done'       },
  failed:     { icon: XCircle,      color: 'text-red-600 bg-red-50',        label: 'Failed'     },
} as const

function StatusBadge({ status }: { status: PipelineJob['status'] }) {
  const { icon: Icon, color, label } = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon className={`h-3.5 w-3.5 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  )
}

// ── Log viewer ────────────────────────────────────────────────────────────────

function LogViewer({ log }: { log: string | null }) {
  const [open, setOpen] = useState(false)
  if (!log) return null
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {open ? 'Hide log' : 'Show log'}
      </button>
      {open && (
        <pre className="mt-1.5 p-3 bg-slate-950 text-slate-300 text-[10px] leading-relaxed rounded-lg overflow-x-auto max-h-60 whitespace-pre-wrap font-mono">
          {log}
        </pre>
      )}
    </div>
  )
}

// ── Job row ───────────────────────────────────────────────────────────────────

const CAD_EXTS_SET = new Set(['dxf', 'dwg', 'obj', 'fbx', 'stl'])

function JobRow({ job, onDelete, onRetry }: {
  job: PipelineJob
  onDelete: (id: string) => void
  onRetry: (id: string) => void
}) {
  const [deleting,  setDeleting]  = useState(false)
  const [retrying,  setRetrying]  = useState(false)
  const isCAD = CAD_EXTS_SET.has(job.input_ext.toLowerCase())

  async function handleDelete() {
    if (!confirm(`Delete job ${job.id}? This will also remove the associated R2 files.`)) return
    setDeleting(true)
    try {
      await deletePipelineJob(job.id)
      onDelete(job.id)
    } catch (err) {
      alert((err as Error).message)
      setDeleting(false)
    }
  }

  async function handleRetry() {
    setRetrying(true)
    try {
      await retryPipelineJob(job.id)
      onRetry(job.id)
    } catch (err) {
      alert((err as Error).message)
    } finally {
      setRetrying(false)
    }
  }

  const isFailed = job.status === 'failed'
  const isDone   = job.status === 'done'

  return (
    <div className={`border rounded-xl p-4 bg-white ${isFailed ? 'border-red-200' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <StatusBadge status={job.status} />
            <span className="font-semibold text-slate-800 text-sm truncate">{job.model_name}</span>
            <span className="text-slate-400 text-xs">· {job.input_ext.toUpperCase()}</span>
            <span className="text-slate-400 text-xs">· {job.wall_height}m walls</span>
            {job.width_ft && job.depth_ft && (
              <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                {job.width_ft}′ × {job.depth_ft}′
              </span>
            )}
          </div>

          {/* CAD files need Blender — not available in cloud runner */}
          {isCAD && isFailed && (
            <div className="flex items-start gap-1.5 mt-1.5 mb-1 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-700">
                CAD files require Blender which is not available in the cloud runner.
                Convert to a floor plan JPG/PNG instead, or run the pipeline locally with Blender installed.
              </p>
            </div>
          )}

          <p className="text-[10px] text-slate-400 font-mono truncate">{job.id}</p>
          <p className="text-xs text-slate-400 mt-0.5">{new Date(job.created_at).toLocaleString()}</p>

          {isDone && job.glb_url && (
            <a
              href={getAssetUrl(job.glb_url)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline"
            >
              <Box className="h-3.5 w-3.5" /> View GLB <ExternalLink className="h-3 w-3" />
            </a>
          )}

          <LogViewer log={job.log} />
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {(isFailed || isDone) && !isCAD && (
            <button
              onClick={handleRetry}
              disabled={retrying}
              title="Retry job"
              className="text-slate-300 hover:text-blue-500 transition-colors disabled:opacity-40 p-1"
            >
              {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={deleting}
            title="Delete job"
            className="text-slate-300 hover:text-red-500 transition-colors disabled:opacity-40 p-1"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Upload form ───────────────────────────────────────────────────────────────

const CAD_EXTS   = ['.dxf', '.dwg', '.obj', '.fbx', '.stl']
const IMAGE_EXTS  = ['.jpg', '.jpeg', '.png']
const ALLOWED_EXTS = [...CAD_EXTS, ...IMAGE_EXTS]

function UploadForm({ models, onJobCreated }: { models: Model[]; onJobCreated: () => void }) {
  const [file, setFile]             = useState<File | null>(null)
  const [modelId, setModelId]       = useState<string>('')
  const [modelName, setModelName]   = useState('')
  const [wallHeight, setWallHeight] = useState('2.8')
  const [widthFt, setWidthFt]       = useState('')
  const [depthFt, setDepthFt]       = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropRef  = useRef<HTMLDivElement>(null)

  const isImage = file ? IMAGE_EXTS.includes('.' + file.name.split('.').pop()?.toLowerCase()) : false

  function handleModelChange(id: string) {
    setModelId(id)
    if (id) {
      const m = models.find((m) => String(m.id) === id)
      if (m) setModelName(m.name)
    }
  }

  function handleFile(f: File) {
    const ext = '.' + f.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_EXTS.includes(ext)) {
      setError(`Unsupported format. Allowed: ${ALLOWED_EXTS.join(', ')}`)
      return
    }
    setFile(f)
    setError(null)
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!file) { setError('Select a file first.'); return }
    if (!modelName.trim()) { setError('Enter a model name.'); return }
    // dims are optional — vectorizer will auto-detect via OCR / door-arc heuristic

    setSubmitting(true)
    setError(null)
    try {
      await createPipelineJob({
        file,
        model_id:   modelId ? Number(modelId) : null,
        model_name: modelName.trim(),
        wall_height: Number(wallHeight) || 2.8,
        ...(isImage && { width_ft: Number(widthFt), depth_ft: Number(depthFt) }),
      })
      setFile(null)
      setWidthFt('')
      setDepthFt('')
      if (inputRef.current) inputRef.current.value = ''
      onJobCreated()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
      <h2 className="text-base font-semibold text-slate-900 mb-4">New Pipeline Job</h2>

      {/* Drop zone */}
      <div
        ref={dropRef}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-slate-200 hover:border-brand-400 rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors mb-4"
      >
        <Upload className="h-6 w-6 text-slate-400" />
        {file ? (
          <span className="text-sm text-slate-700 font-medium">{file.name}</span>
        ) : (
          <>
            <span className="text-sm text-slate-500">Drop CAD or blueprint image here, or click to browse</span>
            <span className="text-xs text-slate-400">CAD: {CAD_EXTS.join(' · ')} &nbsp;·&nbsp; Blueprint images: {IMAGE_EXTS.join(' · ')}</span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_EXTS.join(',')}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
        />
      </div>

      {/* Building dimensions — optional for image uploads (auto-detected via OCR) */}
      {isImage && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-xs font-medium text-blue-800">
              Building dimensions <span className="font-normal text-blue-600">(optional — auto-detected from blueprint text)</span>
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div>
              <label className="block text-xs text-blue-700 mb-1">Width (ft) — override</label>
              <input
                type="number" step="0.1" min="1"
                value={widthFt}
                onChange={(e) => setWidthFt(e.target.value)}
                placeholder="auto"
                className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs text-blue-700 mb-1">Depth (ft) — override</label>
              <input
                type="number" step="0.1" min="1"
                value={depthFt}
                onChange={(e) => setDepthFt(e.target.value)}
                placeholder="auto"
                className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
          <p className="text-[11px] text-blue-500">
            Leave blank to auto-detect from "32′" annotations. Check the job log to confirm detected dimensions. Enter overrides if OCR misreads the drawing.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {/* Link to existing model */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Link to Model (optional)</label>
          <select
            value={modelId}
            onChange={(e) => handleModelChange(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            <option value="">— none —</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>

        {/* Model name */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Model Name *</label>
          <input
            type="text"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            placeholder="e.g. Cascade 24"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>

        {/* Wall height */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Wall Height (m)</label>
          <input
            type="number"
            value={wallHeight}
            onChange={(e) => setWallHeight(e.target.value)}
            min={2.0}
            max={5.0}
            step={0.1}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3">{error}</p>
      )}

      <Button type="submit" disabled={submitting || !file}>
        {submitting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</>
        ) : (
          <><Upload className="h-4 w-4" /> Submit Job</>
        )}
      </Button>
    </form>
  )
}

// ── Runner status panel ───────────────────────────────────────────────────────

function RunnerSetup() {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-xl border mb-6 overflow-hidden border-green-200 bg-green-50">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm font-semibold text-green-800">Pipeline runner active — Fly.io</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-green-200 pt-3 space-y-2">
          <p className="text-xs text-slate-600">
            Blueprint JPG/PNG jobs process automatically. The runner polls every 30s and is pinged instantly on new job creation.
          </p>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <strong>CAD files (DXF, FBX, OBJ, STL)</strong> require Blender and are not supported in the cloud runner.
            Upload a floor plan JPG or PNG instead.
          </p>
          <a href="https://fly.io/apps/lm-pipeline-runner" target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline">
            lm-pipeline-runner.fly.dev <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Pipeline() {
  const [jobs, setJobs]       = useState<PipelineJob[]>([])
  const [models, setModels]   = useState<Model[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter]   = useState<string>('') // '' = all

  const load = useCallback(async () => {
    try {
      const [j, m] = await Promise.all([
        fetchPipelineJobs(filter || undefined),
        fetchAdminModels(),
      ])
      setJobs(j)
      setModels(m)
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }, [filter])

  useEffect(() => { load() }, [load])

  // Auto-refresh while any job is processing
  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === 'pending' || j.status === 'processing')
    if (!hasActive) return
    const t = setInterval(load, 8_000)
    return () => clearInterval(t)
  }, [jobs, load])

  function handleDelete(id: string) {
    setJobs((prev) => prev.filter((j) => j.id !== id))
  }

  function handleRetry(_id: string) {
    load()
  }

  const STATUS_FILTERS = ['', 'pending', 'processing', 'done', 'failed'] as const

  return (
    <AdminLayout>
      <PageHeader
        title="3D Asset Pipeline"
        subtitle="Upload a floor plan JPG or PNG to generate a 3D GLB model automatically"
        action={
          <Button variant="secondary" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        }
      />

      <UploadForm models={models} onJobCreated={load} />

      <RunnerSetup />

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === s
                ? 'bg-brand-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Box className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No jobs yet. Upload a floor plan JPG or PNG to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} onDelete={handleDelete} onRetry={handleRetry} />
          ))}
        </div>
      )}
    </AdminLayout>
  )
}
